import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Button,
  ArrowLeftIcon,
  ArrowRightIcon,
  ToggleIcon,
  Input,
  RadioButton,
  Spinner,
  RadioRatingStarButton,
  EditIcon,
  SearchIconBig
} from '@ag1/ui';
import { Controller, useForm } from 'react-hook-form';
import { useMedia } from '@portal/state/context/MediaContext';
import { useAGZAccount } from '@portal/state/hooks/useAGZAccount';

import {
  FilterOptionValue,
  ProductOptionValue,
  SortOptionValue,
  SortOrderQueryValue,
  V53FlavorOptionValue,
  V53WrittenReview,
  RatingCount
} from '@portal/types/review';
import {
  REVIEW_FILTER_VALUE_TO_FORMULA_VERSION_MAP,
  REVIEW_SORT_VALUE_TO_SORT_BY_MAP,
  REVIEW_SORT_VALUE_TO_SORT_ORDER_MAP
} from '@portal/shared/constants/review';

import Select from '@portal/shared/ui/Select';
import { SSGates } from '@portal/state/context/StatsigContext/StatsigFlags';
import { useGateValue } from '@statsig/react-bindings';
import {
  SKU_TO_PRODUCT_FLAVOR_MAP,
  FILTER_BY_OPTION_LIST,
  SORT_BY_OPTION_LIST,
  WRITTEN_REVIEW_PAGINATION_LIMIT,
  REVIEW_PRODUCT_FLAVOR_OPTION_LIST,
  REVIEW_PRODUCT_FLAVOR_OPTION_LIST_AGZ,
  getUpdatedRatingOptions
} from './constants';
import styles from './V53WrittenReviews.module.scss';
import { ReviewEntry } from './components/ReviewEntry';
import { FilterAndSortModal } from './components/FilterAndSortModal';
import { WriteReviewModal } from './components/WriteReviewModal';
import { useGetLatestPaginatedReviews } from '../hooks/useLatestPaginatedReviews';

interface Props {
  apiResponse: {
    data: {
      reviews: V53WrittenReview[];
      totalCount: number;
      ratingCounts: { rating: number; count: number }[];
      page: number;
      pageSize: number;
      hasMore: boolean;
    } | null;
    isPending: boolean;
    isError: boolean;
  };
  scrollToTop: () => void;
}

export const V53WrittenReviews = ({ apiResponse, scrollToTop }: Props) => {
  const { atSmall, atSSMedium } = useMedia();

  const {
    data: reviewsResponse,
    isPending: isReviewsResponsePending,
    isError: isReviewsResponseErroring
  } = useMemo(() => apiResponse, [apiResponse]);

  const getLatestPaginatedReviews = useGetLatestPaginatedReviews();

  const { register, watch, setValue, control } = useForm({
    defaultValues: {
      sortBy: 'newest',
      sortOrder: 'desc',
      filteredBy: 'all',
      filteredByFlavors: 'all',
      filteredByRating: 'all'
    }
  });

  const {
    sortBy: sortByValue,
    sortOrder: sortOrderValue,
    filteredBy: filteredByValue,
    filteredByFlavors: filteredByFlavorsValue,
    filteredByRating: filteredByRatingValue
  } = watch();

  const [isUpdatingReviewsResponse, setIsUpdatingReviewsResponse] = useState(false);
  const [isUpdatingReviewsFailing, setIsUpdatingReviewsFailing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [isChangingPage, setIsChangingPage] = useState(false);
  const [isGoingNextPage, setIsGoingNextPage] = useState(false);
  const [isGoingPrevPage, setIsGoingPrevPage] = useState(false);

  const [isWriteReviewModalOpen, setIsWriteReviewModalOpen] = useState(false);
  const [isFilterAndSortModalOpen, setIsFilterAndSortModalOpen] = useState(false);

  // input value in real-time
  const [searchTerm, setSearchTerm] = useState('');
  // input value when term is queried with 'Enter'
  const [queriedSearchTerm, setQueriedSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<string | undefined>(undefined);

  const {
    reviews: reviewsData,
    totalCount: totalReviewsCount,
    ratingCounts
  } = useMemo(
    () =>
      reviewsResponse || {
        reviews: [] as V53WrittenReview[],
        totalCount: 0,
        ratingCounts: []
      },
    [reviewsResponse]
  );
  const ratingOptions = getUpdatedRatingOptions(ratingCounts || []);
  const [updatedRatingCounts, setUpdatedRatingCounts] = useState<RatingCount[]>(ratingCounts || []);

  const { isAgzGateEnabled: enableAGZ } = useAGZAccount();
  const enableFlavor = useGateValue(SSGates.enableFlavorSelection);

  const filteredReviewsList = useMemo(() => {
    if (enableAGZ) {
      return reviewsData;
    }
    return reviewsData.filter(
      (reviewData) =>
        !['AGZ', 'AGZ: Berry', 'AGZ: Chocolate', 'AGZ: Chocolate Mint'].includes(
          reviewData.productLabel
        )
    );
  }, [enableAGZ, reviewsData]);

  // open filter & sort side-panel (mobile only)
  const onFilterAndSortButtonClick = useCallback(() => {
    setIsFilterAndSortModalOpen(true);
  }, []);

  // close filter & sort side-panel (mobile only)
  const onCloseFilterAndSortModal = useCallback(() => {
    setIsFilterAndSortModalOpen(false);
  }, []);

  // open write new review side-panel
  const onWriteReviewButtonClick = useCallback(() => {
    setIsWriteReviewModalOpen(true);
  }, []);

  // close write new review side-panel
  const onCloseWriteReviewModal = useCallback(() => {
    setIsWriteReviewModalOpen(false);
  }, []);

  // desktop sort select
  const onSortBySelect = useCallback(
    (event: MouseEvent) => {
      setIsUpdatingReviewsResponse(true);
      getLatestPaginatedReviews({
        parameters: {
          filteredBy:
            REVIEW_FILTER_VALUE_TO_FORMULA_VERSION_MAP[filteredByValue as FilterOptionValue],
          sortBy:
            REVIEW_SORT_VALUE_TO_SORT_BY_MAP[
              (event.target as HTMLInputElement)?.value as SortOptionValue
            ],
          sortOrder:
            REVIEW_SORT_VALUE_TO_SORT_ORDER_MAP[
              (event.target as HTMLInputElement)?.value as SortOptionValue
            ],
          keyword: searchTerm
        },
        onSuccess: () => {
          setIsUpdatingReviewsFailing(false);
          setCurrentPage(1);
          setIsUpdatingReviewsResponse(false);
        },
        onError: () => {
          setIsUpdatingReviewsFailing(true);
          setIsUpdatingReviewsResponse(false);
        }
      });
    },
    [
      getLatestPaginatedReviews,
      searchTerm,
      filteredByValue,
      setCurrentPage,
      setIsUpdatingReviewsResponse,
      setIsUpdatingReviewsFailing
    ]
  );

  // desktop filter select
  const onFilterBySelect = useCallback(
    async (event: string | MouseEvent) => {
      setIsUpdatingReviewsResponse(true);
      setSelectedProduct(event as string);
      const eventValue = enableAGZ
        ? event
        : ((event as MouseEvent).target as HTMLInputElement)?.value;

      getLatestPaginatedReviews({
        parameters: {
          filteredBy: REVIEW_FILTER_VALUE_TO_FORMULA_VERSION_MAP[eventValue as FilterOptionValue],
          sortBy: REVIEW_SORT_VALUE_TO_SORT_BY_MAP[eventValue as SortOptionValue],
          sortOrder: REVIEW_SORT_VALUE_TO_SORT_ORDER_MAP[sortOrderValue as SortOptionValue],
          keyword: searchTerm
        },
        onSuccess: () => {
          setIsUpdatingReviewsFailing(false);
          setCurrentPage(1);
          setIsUpdatingReviewsResponse(false);
        },
        onError: () => {
          setIsUpdatingReviewsFailing(true);
          setIsUpdatingReviewsResponse(false);
        }
      });
    },
    [
      getLatestPaginatedReviews,
      searchTerm,
      sortOrderValue,
      setCurrentPage,
      setIsUpdatingReviewsResponse,
      setIsUpdatingReviewsFailing,
      enableAGZ
    ]
  );

  const onFilterByRatingSelect = useCallback(
    async (product: string, event: MouseEvent) => {
      setIsUpdatingReviewsResponse(true);

      // Search params for sku and rating
      let searchSku;
      let searchRating;

      if (
        (product === 'ag-agz' && filteredByFlavorsValue !== 'all') ||
        (product === 'ag1-next-gen' && filteredByFlavorsValue !== 'all')
      ) {
        searchSku = SKU_TO_PRODUCT_FLAVOR_MAP(
          product as ProductOptionValue,
          filteredByFlavorsValue as V53FlavorOptionValue
        );
      }

      if ((event.target as HTMLInputElement).value !== 'all') {
        searchRating = (event.target as HTMLInputElement).value;
      }

      getLatestPaginatedReviews({
        parameters: {
          filteredBy:
            REVIEW_FILTER_VALUE_TO_FORMULA_VERSION_MAP[filteredByValue as FilterOptionValue],
          sortBy: 'date',
          sortOrder: sortOrderValue as SortOrderQueryValue,
          keyword: searchTerm,
          sku: searchSku,
          rating: searchRating ? Number(searchRating) : undefined
        },
        onSuccess: () => {
          setIsUpdatingReviewsFailing(false);
          setCurrentPage(1);
          setIsUpdatingReviewsResponse(false);
        },
        onError: () => {
          setIsUpdatingReviewsFailing(true);
          setIsUpdatingReviewsResponse(false);
        }
      });
    },
    [
      getLatestPaginatedReviews,
      searchTerm,
      sortOrderValue,
      setCurrentPage,
      filteredByFlavorsValue,
      filteredByValue,
      setIsUpdatingReviewsResponse,
      setIsUpdatingReviewsFailing
    ]
  );

  const onFilterByFlavorSelect = useCallback(
    async (product: string, event: string) => {
      setIsUpdatingReviewsResponse(true);
      setSelectedProduct(product);

      getLatestPaginatedReviews({
        parameters: {
          filteredBy: REVIEW_FILTER_VALUE_TO_FORMULA_VERSION_MAP[product as FilterOptionValue],
          sku:
            event === 'all'
              ? undefined
              : SKU_TO_PRODUCT_FLAVOR_MAP(
                  product as ProductOptionValue,
                  event as V53FlavorOptionValue
                ),
          sortBy: REVIEW_SORT_VALUE_TO_SORT_BY_MAP[event as SortOptionValue],
          sortOrder: REVIEW_SORT_VALUE_TO_SORT_ORDER_MAP[sortOrderValue as SortOptionValue],
          keyword: searchTerm
        },
        onSuccess: () => {
          setIsUpdatingReviewsFailing(false);
          setCurrentPage(1);
          setIsUpdatingReviewsResponse(false);
        },
        onError: () => {
          setIsUpdatingReviewsFailing(true);
          setIsUpdatingReviewsResponse(false);
        }
      });
    },
    [
      getLatestPaginatedReviews,
      searchTerm,
      sortOrderValue,
      setCurrentPage,
      setIsUpdatingReviewsResponse,
      setIsUpdatingReviewsFailing
    ]
  );

  // mobile apply filter & sort logic
  const onApplyMobileFilterAndSort = useCallback(
    ({
      modalSortBy,
      modalFilteredBy,
      modalFilteredByFlavors,
      modalFilteredByRating
    }: {
      modalSortBy: string;
      modalFilteredBy: string;
      modalFilteredByFlavors?: string;
      modalFilteredByRating?: string;
    }) => {
      setIsUpdatingReviewsResponse(true);
      getLatestPaginatedReviews({
        parameters: {
          filteredBy:
            REVIEW_FILTER_VALUE_TO_FORMULA_VERSION_MAP[modalFilteredBy as FilterOptionValue],
          sortBy: REVIEW_SORT_VALUE_TO_SORT_BY_MAP[sortByValue as SortOptionValue],
          sortOrder: REVIEW_SORT_VALUE_TO_SORT_ORDER_MAP[modalSortBy as SortOptionValue],
          sku:
            modalFilteredByFlavors === 'all'
              ? undefined
              : SKU_TO_PRODUCT_FLAVOR_MAP(
                  modalFilteredBy as ProductOptionValue,
                  modalFilteredByFlavors as V53FlavorOptionValue
                ),
          rating: modalFilteredByRating === 'all' ? undefined : Number(modalFilteredByRating),
          keyword: searchTerm
        },
        onSuccess: () => {
          setIsUpdatingReviewsFailing(false);
          setValue('sortBy', modalSortBy);
          setValue('filteredBy', modalFilteredBy);
          setValue('filteredByFlavors', modalFilteredByFlavors || 'all');
          setValue('filteredByRating', modalFilteredByRating || 'all');
          setCurrentPage(1);
          setIsUpdatingReviewsResponse(false);
        },
        onError: () => {
          setIsUpdatingReviewsFailing(true);
          setValue('sortBy', modalSortBy);
          setValue('filteredBy', modalFilteredBy);
          setValue('filteredByFlavors', modalFilteredByFlavors || 'all');
          setValue('filteredByRating', modalFilteredByRating || 'all');
          setIsUpdatingReviewsResponse(false);
        }
      });
    },
    [
      getLatestPaginatedReviews,
      sortByValue,
      setValue,
      searchTerm,
      setCurrentPage,
      setIsUpdatingReviewsResponse,
      setIsUpdatingReviewsFailing
    ]
  );

  // keep track of search keyword
  const onSearchTermChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  // desktop & mobile search query logic
  const onSearch = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;

      setQueriedSearchTerm(searchTerm);
      setIsUpdatingReviewsResponse(true);

      getLatestPaginatedReviews({
        parameters: {
          filteredBy:
            REVIEW_FILTER_VALUE_TO_FORMULA_VERSION_MAP[filteredByValue as FilterOptionValue],
          sortBy:
            REVIEW_SORT_VALUE_TO_SORT_BY_MAP[
              (event.target as HTMLInputElement)?.value as SortOptionValue
            ],
          sortOrder: REVIEW_SORT_VALUE_TO_SORT_ORDER_MAP[sortOrderValue as SortOptionValue],
          keyword: searchTerm
        },
        onSuccess: () => {
          setIsUpdatingReviewsFailing(false);
          setCurrentPage(1);
          setIsUpdatingReviewsResponse(false);
        },
        onError: () => {
          setIsUpdatingReviewsFailing(true);
          setIsUpdatingReviewsResponse(false);
        }
      });
    },
    [
      getLatestPaginatedReviews,
      searchTerm,
      setQueriedSearchTerm,
      filteredByValue,
      sortOrderValue,
      setCurrentPage,
      setIsUpdatingReviewsResponse,
      setIsUpdatingReviewsFailing
    ]
  );

  // reset search keyword, and return reviews to current filter & sort selections
  const onSearchReset = useCallback(() => {
    setSearchTerm('');
    setQueriedSearchTerm('');
    setIsUpdatingReviewsResponse(true);

    getLatestPaginatedReviews({
      parameters: {
        filteredBy:
          REVIEW_FILTER_VALUE_TO_FORMULA_VERSION_MAP[filteredByValue as FilterOptionValue],
        sortBy: REVIEW_SORT_VALUE_TO_SORT_BY_MAP[sortByValue as SortOptionValue],
        sortOrder: REVIEW_SORT_VALUE_TO_SORT_ORDER_MAP[sortByValue as SortOptionValue],
        keyword: ''
      },
      onSuccess: () => {
        setIsUpdatingReviewsFailing(false);
        setCurrentPage(1);
        setIsUpdatingReviewsResponse(false);
      },
      onError: () => {
        setIsUpdatingReviewsFailing(true);
        setIsUpdatingReviewsResponse(false);
      }
    });
  }, [
    setSearchTerm,
    setQueriedSearchTerm,
    getLatestPaginatedReviews,
    filteredByValue,
    sortByValue,
    setCurrentPage,
    setIsUpdatingReviewsResponse,
    setIsUpdatingReviewsFailing
  ]);

  // update visible reviews to the appropriate page
  const onPageChange = useCallback(
    (newPage: number) => {
      setIsChangingPage(true);

      if (newPage < currentPage) setIsGoingPrevPage(true);
      else if (newPage > currentPage) setIsGoingNextPage(true);

      getLatestPaginatedReviews({
        parameters: {
          page: newPage,
          filteredBy:
            REVIEW_FILTER_VALUE_TO_FORMULA_VERSION_MAP[filteredByValue as FilterOptionValue],
          sortBy: REVIEW_SORT_VALUE_TO_SORT_BY_MAP[sortByValue as SortOptionValue],
          sortOrder: REVIEW_SORT_VALUE_TO_SORT_ORDER_MAP[sortByValue as SortOptionValue],
          keyword: searchTerm
        },
        onSuccess: () => {
          setIsUpdatingReviewsFailing(false);
          setCurrentPage(newPage);
          setIsChangingPage(false);
          if (newPage < currentPage) setIsGoingPrevPage(false);
          else if (newPage > currentPage) setIsGoingNextPage(false);
          scrollToTop();
        },
        onError: () => {
          setIsUpdatingReviewsFailing(true);
          if (newPage < currentPage) setIsGoingPrevPage(false);
          else if (newPage > currentPage) setIsGoingNextPage(false);
          setIsUpdatingReviewsResponse(false);
          scrollToTop();
        }
      });
    },
    [
      getLatestPaginatedReviews,
      filteredByValue,
      sortByValue,
      searchTerm,
      scrollToTop,
      currentPage,
      setIsChangingPage,
      setIsUpdatingReviewsFailing
    ]
  );

  const onMobileFilterBySelect = useCallback(
    async ({
      filteredBy,
      filteredByFlavors
    }: {
      filteredBy: string;
      filteredByFlavors: string;
    }) => {
      try {
        const response = await getLatestPaginatedReviews({
          parameters: {
            filteredBy: REVIEW_FILTER_VALUE_TO_FORMULA_VERSION_MAP[filteredBy as FilterOptionValue],
            sku:
              filteredByFlavors === 'all'
                ? undefined
                : SKU_TO_PRODUCT_FLAVOR_MAP(
                    filteredBy as ProductOptionValue,
                    filteredByFlavors as V53FlavorOptionValue
                  )
          }
        });

        setUpdatedRatingCounts(response?.ratingCounts ?? []);
      } catch (error) {
        setUpdatedRatingCounts([]);
      }
    },
    [getLatestPaginatedReviews, setUpdatedRatingCounts]
  );

  const showPagination = totalReviewsCount > WRITTEN_REVIEW_PAGINATION_LIMIT;

  if (isReviewsResponsePending) {
    return <Spinner className={styles.loading} withOverlay={false} />;
  }

  return (
    <div className={styles.container}>
      {/* desktop Filters & Sort */}
      <div className={styles.filters}>
        <div className={styles.filterContent}>
          <div className={clsx(styles.filterGroup, styles.sortByGroup)}>
            <h4>Sort by</h4>
            {SORT_BY_OPTION_LIST.map((option) => (
              <RadioButton
                key={option.id}
                className={clsx(styles.radio, option.value === sortByValue && styles.isSelected)}
                {...register('sortBy')}
                {...option}
                onClick={onSortBySelect}
              />
            ))}
          </div>
          <div className={clsx(styles.filterGroup, styles.filteredByGroup)}>
            {enableFlavor && (
              <>
                <h4>What product(s) would you like to review?</h4>
                <Controller
                  name="filteredBy"
                  control={control}
                  render={({ field: { onChange, value: formValue, ...rest } }) => (
                    <Select
                      {...rest}
                      id="filteredBy"
                      placeholder="Select Product"
                      options={
                        enableAGZ
                          ? FILTER_BY_OPTION_LIST
                          : FILTER_BY_OPTION_LIST.filter((option) => option.value !== 'ag-agz')
                      }
                      value={formValue}
                      onChange={(value: string) => {
                        onChange(value);
                        setSelectedProduct(value);
                        onFilterBySelect(value);
                      }}
                      className={clsx(
                        styles.filterBySelect,
                        formValue === filteredByValue && styles.isSelected
                      )}
                    />
                  )}
                />
              </>
            )}
            {((selectedProduct === 'ag1-next-gen' && enableFlavor) ||
              (selectedProduct === 'ag-agz' && enableAGZ)) && (
              <div className={styles.flavorFilter}>
                <h4>{`What flavor(s) of ${FILTER_BY_OPTION_LIST.find(
                  (option) => option.value === selectedProduct
                )?.label} would you like to view?`}</h4>
                {(selectedProduct === 'ag1-next-gen'
                  ? REVIEW_PRODUCT_FLAVOR_OPTION_LIST
                  : REVIEW_PRODUCT_FLAVOR_OPTION_LIST_AGZ
                ).map((option) => (
                  <Controller
                    key={option.id}
                    name="filteredByFlavors"
                    control={control}
                    render={({ field: { onChange, ...rest } }) => (
                      <RadioButton
                        key={option.id}
                        className={clsx(
                          styles.radio,
                          option.value === filteredByFlavorsValue && styles.isSelected
                        )}
                        {...rest}
                        {...option}
                        onChange={(e) => {
                          onChange(e.target.value);
                          onFilterByFlavorSelect(selectedProduct, e.target.value);
                        }}
                      />
                    )}
                  />
                ))}
              </div>
            )}
          </div>

          {enableAGZ && (
            <div className={styles.flavorFilter}>
              <h4>Rating</h4>
              {ratingOptions.map((option) => (
                <Controller
                  key={option.id}
                  name="filteredByRating"
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <RadioRatingStarButton
                      key={option.id}
                      rating={option.value !== 'all' ? option.value : undefined}
                      ratingLabel={option.count.toString()}
                      className={clsx(
                        styles.radio,
                        option.value === filteredByRatingValue && styles.isSelected
                      )}
                      {...rest}
                      {...option}
                      onChange={(e) => {
                        onChange(e.target.value);
                      }}
                      onClick={(e) => {
                        onFilterByRatingSelect(selectedProduct as string, e);
                      }}
                    />
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={styles.reviewBody}>
        {/* mobile "Filter & Sort" and "Write Review" btns */}
        {!atSSMedium && (
          <div className={clsx(styles.userActionGroup, styles.mobile)}>
            <Button
              className={styles.filterAndSortButton}
              variant="link"
              icon={<ToggleIcon size={24} />}
              onClick={onFilterAndSortButtonClick}>
              Filter & Sort
            </Button>
            <Button
              className={styles.writeReviewButton}
              icon={atSmall ? <EditIcon /> : null}
              iconPosition="right"
              onClick={onWriteReviewButtonClick}
              size={{
                xs: 'small'
              }}
              rounded>
              Write Review
            </Button>
          </div>
        )}
        <div className={styles.userActionGroup}>
          {/* search bar */}
          <Input
            className={styles.searchBar}
            placeholder="Search reviews..."
            value={searchTerm}
            onChange={onSearchTermChange}
            onKeyDown={onSearch}
            renderAddon={() => <SearchIconBig />}
          />
          {/* desktop "Write Review" btn */}
          {atSSMedium && (
            <Button
              className={styles.writeReviewButton}
              icon={<EditIcon />}
              iconPosition="right"
              size="medium"
              onClick={onWriteReviewButtonClick}
              rounded>
              Write Review
            </Button>
          )}
        </div>
        {/* review list body */}
        {isUpdatingReviewsResponse ? (
          <Spinner className={styles.contentLoading} withOverlay={false} />
        ) : (
          <>
            {isReviewsResponseErroring || isUpdatingReviewsFailing ? (
              <div className={styles.errorContent}>
                There was an error while fetching reviews. Please try again later.
              </div>
            ) : (
              <div className={styles.reviewEntries}>
                {reviewsData.length ? (
                  filteredReviewsList.map((reviewData) => (
                    <ReviewEntry
                      key={reviewData.id}
                      searchTerm={queriedSearchTerm}
                      {...reviewData}
                    />
                  ))
                ) : (
                  <div className={styles.emptyReviews}>
                    <h6>No matching reviews</h6>
                    <p>Try clearing or changing the filters.</p>
                    <Button onClick={onSearchReset} size="small" rounded>
                      Clear Search
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {/* pagination */}
        {showPagination && (
          <div className={styles.navigator}>
            <Button
              variant="outline"
              icon={<ArrowLeftIcon />}
              iconPosition="left"
              onClick={() => onPageChange(currentPage - 1)}
              size={{
                xxs: 'small',
                xs: 'medium'
              }}
              disabled={isUpdatingReviewsResponse || isChangingPage || currentPage === 1}>
              {isGoingPrevPage ? (
                <Spinner className={styles.buttonLoading} withOverlay={false} size={24} />
              ) : (
                'Prev Page'
              )}
            </Button>
            <Button
              variant="outline"
              icon={<ArrowRightIcon />}
              iconPosition="right"
              onClick={() => onPageChange(currentPage + 1)}
              size={{
                xxs: 'small',
                xs: 'medium'
              }}
              disabled={
                isUpdatingReviewsResponse ||
                isChangingPage ||
                currentPage > Math.ceil(totalReviewsCount / WRITTEN_REVIEW_PAGINATION_LIMIT)
              }>
              {isGoingNextPage ? (
                <Spinner className={styles.buttonLoading} withOverlay={false} size={24} />
              ) : (
                'Next Page'
              )}
            </Button>
          </div>
        )}
      </div>
      {/* mobile Filter & Sort modal */}
      {isFilterAndSortModalOpen && (
        <FilterAndSortModal
          isOpen={isFilterAndSortModalOpen}
          onClose={onCloseFilterAndSortModal}
          onApply={onApplyMobileFilterAndSort}
          form={{
            initialValues: {
              sortByValue,
              filteredByValue,
              filteredByFlavorsValue,
              filteredByRatingValue
            }
          }}
          ratingCounts={updatedRatingCounts}
          onMobileFilterBySelect={onMobileFilterBySelect}
        />
      )}
      {/* write review modal */}
      {isWriteReviewModalOpen && (
        <WriteReviewModal isOpen={isWriteReviewModalOpen} onClose={onCloseWriteReviewModal} />
      )}
    </div>
  );
};
